// helpContent — the typed docs read-model + seed content for the Help overlay.
//
// Promoted from `sketches/help/helpContent.ts` (in-app-docs.md §5). This is
// the shape docs land in AFTER the shell parses bundled markdown at the
// boundary — the pure read-model the reading pane and search fold over. The
// load-bearing idea: the Diátaxis quadrant is a CLOSED 4-case discriminated
// union, and each variant carries ITS OWN body shape (a tutorial has steps, a
// how-to has a recipe, reference has rows, explanation has prose). The four
// reading modes ARE four types — so the overlay renders each with an
// exhaustive `switch (doc.quadrant)`, never an if-ladder, and an illegal mode
// (reference-with-steps) is unrepresentable.
//
// Pure data, no I/O. SHORTCUT (lifecycle): the seed content below is
// hand-written English, not yet parsed from `docs/help/**.md` — the shell-side
// markdown parser that would produce this shape is future work; today the
// array IS the read-model.
//
// i18n: every user-facing string is a Lingui `msg` descriptor (pure data,
// extracted into en.po/sv.po like any macro). The quadrant labels/blurbs stay
// descriptors the component localizes; the doc bodies are built by
// `helpDocs(translate)`, so search folds over the reader's own language.

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { IconName } from "../Icon/Icon.js";

// The four orientations — closed at four (Diátaxis is not extensible), so the
// union is total and every consumer's switch is exhaustive.
export type Quadrant = "tutorial" | "how_to" | "reference" | "explanation";

// Tutorial — a linear thread of steps. No search inside; you finish or quit.
export type TutorialStep = {
    readonly title: string;
    readonly body: string;
};

// How-to — one recipe: assume the reader knows what the nouns ARE.
export type Recipe = {
    readonly steps: readonly string[];
    readonly result: string;
};

// Reference — dry rows, browse-not-read.
export type RefRow = {
    readonly term: string;
    readonly def: string;
};

type DocBase = {
    readonly id: string;
    readonly title: string;
    // One-line scent for the browse list — the searchable surface.
    readonly summary: string;
};

export type TutorialDoc = DocBase & {
    readonly quadrant: "tutorial";
    readonly steps: readonly TutorialStep[];
};

export type HowToDoc = DocBase & {
    readonly quadrant: "how_to";
    // Free tags so "insert" recipes cluster for a future palette-hint deep-link.
    readonly tags: readonly string[];
    readonly recipe: Recipe;
};

export type ReferenceDoc = DocBase & {
    readonly quadrant: "reference";
    readonly rows: readonly RefRow[];
};

export type ExplanationDoc = DocBase & {
    readonly quadrant: "explanation";
    // Paragraphs of reading-mode prose. No commands, no steps.
    readonly prose: readonly string[];
};

// The sum: one entry is exactly one quadrant, with that quadrant's body shape.
export type DocEntry = TutorialDoc | HowToDoc | ReferenceDoc | ExplanationDoc;

// Per-quadrant UI language descriptor — the affordances that differ by mode
// (Diátaxis: search + outbound links ON for how-to/reference, OFF for
// tutorial/explanation). Data, so the overlay reads it instead of branching.
// `label`/`blurb` are Lingui MessageDescriptors, localized by the component
// via `i18n._()`.
export type QuadrantMeta = {
    readonly key: Quadrant;
    readonly label: MessageDescriptor;
    readonly icon: IconName;
    readonly blurb: MessageDescriptor;
    // In-tab search is enabled only where browse-not-read is the mode.
    readonly searchable: boolean;
};

export const QUADRANTS: readonly QuadrantMeta[] = [
    {
        key: "tutorial",
        label: msg`Tutorials`,
        icon: "play",
        blurb: msg`Guided first journeys — learn by doing.`,
        searchable: false,
    },
    {
        key: "how_to",
        label: msg`How-to guides`,
        icon: "check",
        blurb: msg`Task recipes for getting something done.`,
        searchable: true,
    },
    {
        key: "reference",
        label: msg`Reference`,
        icon: "database",
        blurb: msg`Exhaustive facts to look up.`,
        searchable: true,
    },
    {
        key: "explanation",
        label: msg`Explanation`,
        icon: "info",
        blurb: msg`The why behind how it works.`,
        searchable: false,
    },
];

export const quadrantMeta = (key: Quadrant): QuadrantMeta => {
    const found = QUADRANTS.find((q) => q.key === key);
    // The union is closed and QUADRANTS covers all four, so this is total;
    // the fallback only satisfies the type-checker for the impossible branch.
    return found ?? QUADRANTS[0];
};

// Seed content — a thin, real-vocabulary slice (Source / Material / Package /
// Template / Constraint), enough to exercise every reading mode — swap for the
// real parsed corpus when `docs/help/**.md` lands. `translate` localizes each
// descriptor (the shell passes `i18n._`); the function itself stays pure.
export const helpDocs = (
    translate: (descriptor: MessageDescriptor) => string,
): readonly DocEntry[] => [
    {
        quadrant: "tutorial",
        id: "first-material",
        title: translate(msg`From a source to an exported package`),
        summary: translate(
            msg`The 2-minute first journey: ingest, generate, export.`,
        ),
        steps: [
            {
                title: translate(msg`Add a source`),
                body: translate(
                    msg`Drop a PDF into Uploaded sources. The app ingests it as a Source — the raw material the assistant will draw on.`,
                ),
            },
            {
                title: translate(msg`Generate a material`),
                body: translate(
                    msg`Ask the assistant to make a quiz from the source. It proposes; you approve. The result is a Material.`,
                ),
            },
            {
                title: translate(msg`Package and export`),
                body: translate(
                    msg`Choose a Format and export. The Material becomes a Package a learner can open.`,
                ),
            },
        ],
    },
    {
        quadrant: "how_to",
        id: "insert-a-block",
        title: translate(msg`Insert a block in the editor`),
        summary: translate(
            msg`Add a heading, paragraph, question or media to a material.`,
        ),
        tags: ["editor", "insert", "block", "palette"],
        recipe: {
            steps: [
                translate(msg`Place the caret on an empty line.`),
                translate(msg`Press “/” (or open the insert palette).`),
                translate(
                    msg`Type the block’s name, or pick it from the list.`,
                ),
                translate(msg`Press Enter to insert.`),
            ],
            result: translate(
                msg`The block appears at the caret, ready to edit.`,
            ),
        },
    },
    {
        quadrant: "how_to",
        id: "add-media",
        title: translate(msg`Add an image or video`),
        summary: translate(
            msg`Insert media — only where the template’s Shape allows it.`,
        ),
        tags: ["editor", "insert", "media", "palette", "image", "video"],
        recipe: {
            steps: [
                translate(msg`Open the insert palette on an empty line.`),
                translate(
                    msg`Scroll to the Media group (shown only if this template permits media).`,
                ),
                translate(msg`Pick Image or Video and drop in the file.`),
            ],
            result: translate(
                msg`The media block is added. If Media is absent, the template forbids it — ask the assistant why.`,
            ),
        },
    },
    {
        quadrant: "how_to",
        id: "export-package",
        title: translate(msg`Export a package for your LMS`),
        summary: translate(
            msg`Turn a finished material into a deliverable package.`,
        ),
        tags: ["package", "export", "format"],
        recipe: {
            steps: [
                translate(msg`Open the material you want to ship.`),
                translate(msg`Choose Export and pick a Format.`),
                translate(
                    msg`Confirm — export leaves the app, so it is always approval-gated.`,
                ),
            ],
            result: translate(msg`A Package is produced in the chosen Format.`),
        },
    },
    {
        quadrant: "reference",
        id: "glossary",
        title: translate(msg`Glossary`),
        summary: translate(msg`Canonical definitions of the core nouns.`),
        rows: [
            {
                term: translate(
                    msg({ message: "Source", context: "glossary term" }),
                ),
                def: translate(
                    msg`Raw ingested material (a PDF, a doc) the assistant draws on.`,
                ),
            },
            {
                term: translate(
                    msg({ message: "Material", context: "glossary term" }),
                ),
                def: translate(
                    msg`A generated learning artifact — a quiz, a lesson.`,
                ),
            },
            {
                term: translate(
                    msg({ message: "Package", context: "glossary term" }),
                ),
                def: translate(
                    msg`A material rendered into a deliverable Format.`,
                ),
            },
            {
                term: translate(
                    msg({ message: "Template", context: "glossary term" }),
                ),
                def: translate(
                    msg`The Shape that decides which blocks a material may contain.`,
                ),
            },
            {
                term: translate(
                    msg({ message: "Constraint", context: "glossary term" }),
                ),
                def: translate(
                    msg`A quality rule a material is checked against.`,
                ),
            },
            {
                term: translate(
                    msg({
                        message: "Learning Context",
                        context: "glossary term",
                    }),
                ),
                def: translate(
                    msg`Who the material is for — the audience and its facets.`,
                ),
            },
        ],
    },
    {
        quadrant: "explanation",
        id: "two-stage-pipeline",
        title: translate(msg`Why the two-stage pipeline`),
        summary: translate(
            msg`Generation and packaging are separated on purpose.`,
        ),
        prose: [
            translate(
                msg`The app splits authoring into two stages: first you and the assistant produce a Material — the pedagogy, the questions, the prose. Only later do you package that Material into a Format for a particular learner context.`,
            ),
            translate(
                msg`Keeping them apart means one Material can become many Packages — a quiz exported to an LMS today, a printable worksheet tomorrow — without regenerating the pedagogy each time. The Material is the durable artifact; the Package is a disposable projection of it.`,
            ),
            translate(
                msg`It also draws a clean line for the assistant: generating content is additive and recoverable, while packaging leaves the app and is always approval-gated.`,
            ),
        ],
    },
];

// Pure search — a fold over the seed. Matches title + summary + (tags | terms),
// the same substrate the omnibar would reuse. No I/O, no state.
export const searchDocs = (
    docs: readonly DocEntry[],
    query: string,
): readonly DocEntry[] => {
    const q = query.trim().toLowerCase();
    if (q === "") return [];
    return docs.filter((doc) => {
        const extra =
            doc.quadrant === "how_to"
                ? doc.tags.join(" ")
                : doc.quadrant === "reference"
                  ? doc.rows.map((r) => `${r.term} ${r.def}`).join(" ")
                  : "";
        return `${doc.title} ${doc.summary} ${extra}`.toLowerCase().includes(q);
    });
};
